import {
  Alert,
  Badge,
  Button,
  Code,
  Divider,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { useT } from '../i18n/useT.js';
import type { MessageKey } from '../i18n/da.js';
import { formatDate, formatMoney } from '../format.js';
import {
  useDeleteInvoice,
  useEInvoiceDispatches,
  useEInvoiceDocument,
  useInvoice,
  useIssueInvoice,
  useMarkInvoicePaid,
  useSendEInvoice,
} from '../api/hooks.js';
import type { EInvoiceFormat, Invoice, SendEInvoiceResult } from '../api/hooks.js';

/**
 * One invoice, and everything that can be done to it.
 *
 * The two irreversible actions are separated from the reversible ones by more
 * than layout: each states what it does before it is pressed, because neither
 * can be undone afterwards.
 */

const FORMAT_OPTIONS = [
  { value: 'oioubl', labelKey: 'invDetail.format.oioubl' as MessageKey },
  { value: 'peppol_bis', labelKey: 'invDetail.format.peppol' as MessageKey },
];

export interface InvoiceDetailModalProps {
  readonly invoice: Invoice | null;
  readonly opened: boolean;
  readonly onClose: () => void;
}

export function InvoiceDetailModal({
  invoice,
  opened,
  onClose,
}: InvoiceDetailModalProps): ReactNode {
  const t = useT();
  const detail = useInvoice(invoice === null ? null : invoice.id);
  const current = detail.data ?? invoice;
  const isDraft = current !== null && current.status === 'draft';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={
        current === null
          ? ''
          : current.number === null
            ? t('invDetail.draftTitle')
            : t('invDetail.title', { number: current.number })
      }
    >
      <Stack gap="md">
        {detail.isError && <ErrorMessage error={detail.error} />}

        {current !== null && (
          <>
            <Group justify="space-between" wrap="wrap">
              <Stack gap={2}>
                <Text size="sm" c="dimmed">
                  {current.contactName ?? t('invDetail.noCustomer')}
                </Text>
                <Text size="sm">
                  {t('invDetail.dates', {
                    date: formatDate(current.date),
                    dueDate: formatDate(current.dueDate),
                  })}
                </Text>
              </Stack>
              <Stack gap={2} align="flex-end">
                <Text size="sm" c="dimmed">
                  {t('invDetail.total')}
                </Text>
                <Text className="money-cell" fw={700} size="lg">
                  {formatMoney(current.total)}
                </Text>
              </Stack>
            </Group>

            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('draft.col.description')}</Table.Th>
                  <Table.Th style={{ width: 80 }}>{t('draft.col.quantity')}</Table.Th>
                  <Table.Th style={{ width: 120 }} className="money-cell">
                    {t('draft.col.unitPrice')}
                  </Table.Th>
                  <Table.Th style={{ width: 90 }}>{t('journal.col.vat')}</Table.Th>
                  <Table.Th style={{ width: 130 }} className="money-cell">
                    {t('bank.col.amount')}
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {current.lines.map((line) => (
                  <Table.Tr key={line.id}>
                    <Table.Td>
                      <Text size="sm">{line.description}</Text>
                      {line.accountCode !== null && (
                        <Text size="xs" c="dimmed" ff="monospace">
                          {line.accountCode}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatMoney(line.quantity, 0)}</Text>
                    </Table.Td>
                    <Table.Td className="money-cell">
                      {formatMoney(line.unitPrice)}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{line.vatCode ?? ''}</Text>
                    </Table.Td>
                    <Table.Td className="money-cell">
                      {formatMoney(line.lineTotal)}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            <Group justify="flex-end" gap="lg">
              <Text size="sm" c="dimmed">
                {t('invDetail.subtotal', { amount: formatMoney(current.subtotal) })}
              </Text>
              <Text size="sm" c="dimmed">
                {t('invDetail.vat', { amount: formatMoney(current.vatAmount) })}
              </Text>
            </Group>

            {isDraft ? (
              <DraftActions invoice={current} onDeleted={onClose} />
            ) : (
              <IssuedActions invoice={current} />
            )}
          </>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            {t('invDetail.close')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// -----------------------------------------------------------------------------
// While it is still a draft
// -----------------------------------------------------------------------------

function DraftActions({
  invoice,
  onDeleted,
}: {
  readonly invoice: Invoice;
  readonly onDeleted: () => void;
}): ReactNode {
  const t = useT();
  const issue = useIssueInvoice();
  const remove = useDeleteInvoice();
  const [confirming, setConfirming] = useState(false);

  const missingAccount = invoice.lines.some((line) => line.accountCode === null);

  return (
    <>
      <Divider label={t('invDetail.draft')} labelPosition="left" />

      {!confirming && (
        <Group justify="space-between">
          <Button
            variant="subtle"
            color="red"
            loading={remove.isPending}
            onClick={() => {
              remove.mutate(invoice.id, {
                onSuccess: () => {
                  notifications.show({
                    color: 'green',
                    title: t('invDetail.draftDeleted'),
                    message: t('invDetail.draftDeletedDetail'),
                  });
                  onDeleted();
                },
              });
            }}
          >
            {t('invDetail.deleteDraft')}
          </Button>
          <Button
            onClick={() => {
              setConfirming(true);
            }}
          >
            {t('invDetail.issue')}
          </Button>
        </Group>
      )}

      {confirming && (
        <Stack gap="sm">
          {/*
            The whole point of this step. Issuing allocates a number from a run
            the law requires to be unbroken and posts to a ledger that cannot be
            edited — both in one transaction. Saying "are you sure?" would ask
            for a decision without giving the facts it turns on.
          */}
          <Alert color="orange" variant="light" title={t('invDetail.irreversibleTitle')}>
            <Stack gap="xs">
              <Text size="sm">{t('invDetail.irreversible1')}</Text>
              <Text size="sm">{t('invDetail.irreversible2')}</Text>
            </Stack>
          </Alert>

          {missingAccount && (
            <Alert color="yellow" variant="light" title={t('invDetail.missingAccountTitle')}>
              {t('invDetail.missingAccount')}
            </Alert>
          )}

          {issue.isError && (
            <ErrorMessage error={issue.error} title={t('invDetail.issueFailed')} />
          )}

          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => {
                setConfirming(false);
              }}
            >
              {t('invDetail.undo')}
            </Button>
            <Button
              color="orange"
              loading={issue.isPending}
              onClick={() => {
                issue.mutate(invoice.id, {
                  onSuccess: (issued) => {
                    notifications.show({
                      color: 'green',
                      title: t('invDetail.issued', { number: issued.number ?? '' }),
                      message: t('invDetail.issuedDetail'),
                    });
                    setConfirming(false);
                  },
                });
              }}
            >
              {t('invDetail.issueAndPost')}
            </Button>
          </Group>
        </Stack>
      )}

      {remove.isError && (
        <ErrorMessage error={remove.error} title={t('invDetail.deleteFailed')} />
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
// Once it is issued
// -----------------------------------------------------------------------------

function IssuedActions({ invoice }: { readonly invoice: Invoice }): ReactNode {
  const t = useT();
  const markPaid = useMarkInvoicePaid();
  const send = useSendEInvoice();
  const document = useEInvoiceDocument();
  const dispatches = useEInvoiceDispatches({ invoiceId: invoice.id });
  const [format, setFormat] = useState<EInvoiceFormat>('oioubl');
  const [result, setResult] = useState<SendEInvoiceResult | null>(null);

  return (
    <>
      <Divider label={t('invDetail.issuedSection')} labelPosition="left" />

      <Alert color="gray" variant="light" title={t('invDetail.lockedTitle')}>
        {t('invDetail.locked')}
      </Alert>

      <Group>
        {invoice.status !== 'paid' && (
          <Button
            variant="light"
            loading={markPaid.isPending}
            onClick={() => {
              markPaid.mutate(invoice.id, {
                onSuccess: () => {
                  notifications.show({
                    color: 'green',
                    title: t('invDetail.markedPaid'),
                    message: invoice.number ?? '',
                  });
                },
              });
            }}
          >
            {t('invDetail.markPaid')}
          </Button>
        )}
      </Group>

      {markPaid.isError && <ErrorMessage error={markPaid.error} />}

      <Divider label={t('invDetail.eInvoice')} labelPosition="left" />

      <Group align="flex-end" wrap="wrap">
        <Select
          label={t('invDetail.format')}
          data={FORMAT_OPTIONS.map((option) => ({
            value: option.value,
            label: t(option.labelKey),
          }))}
          allowDeselect={false}
          value={format}
          onChange={(value) => {
            setFormat((value ?? 'oioubl') as EInvoiceFormat);
          }}
          maw={260}
        />
        <Button
          variant="light"
          loading={document.isPending}
          onClick={() => {
            document.mutate(
              { id: invoice.id, format },
              {
                onSuccess: () => {
                  notifications.show({
                    color: 'blue',
                    title: t('invDetail.documentBuilt'),
                    message: t('invDetail.documentBuiltDetail'),
                  });
                },
              },
            );
          }}
        >
          {t('invDetail.showDocument')}
        </Button>
        <Button
          loading={send.isPending}
          onClick={() => {
            setResult(null);
            send.mutate(
              { id: invoice.id, format },
              {
                onSuccess: (sent) => {
                  setResult(sent);
                },
              },
            );
          }}
        >
          {t('invDetail.send')}
        </Button>
      </Group>

      <Text size="xs" c="dimmed">
        {t('invDetail.buildNote')}
      </Text>

      {document.isError && (
        <ErrorMessage error={document.error} title={t('invDetail.documentFailed')} />
      )}
      {document.isSuccess && (
        <Code block style={{ maxHeight: 220, overflow: 'auto' }}>
          {document.data}
        </Code>
      )}

      {send.isError && (
        <ErrorMessage error={send.error} title={t('invDetail.sendFailed')} />
      )}

      {result !== null && <SendResult result={result} />}

      {dispatches.isSuccess && dispatches.data.items.length > 0 && (
        <>
          <Text size="sm" fw={500}>
            {t('invDetail.dispatches')}
          </Text>
          <Table>
            <Table.Tbody>
              {dispatches.data.items.map((dispatch) => (
                <Table.Tr key={dispatch.id}>
                  <Table.Td>
                    <Text size="sm">{formatDate(dispatch.requestedAt)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{dispatch.format}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {dispatch.provider}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      size="sm"
                      variant="light"
                      color={
                        dispatch.status === 'sent'
                          ? 'blue'
                          : dispatch.status === 'failed'
                            ? 'red'
                            : 'gray'
                      }
                    >
                      {dispatch.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {dispatch.error !== null && (
                      <Text size="xs" c="red">
                        {dispatch.error}
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </>
      )}
    </>
  );
}

/**
 * What actually happened when Send was pressed.
 *
 * **`delivered`, not `status`.** With no access point configured the dry-run
 * provider builds, checks, hashes and records the dispatch exactly as a live
 * send would, and returns `status: sent` — while opening no socket. Both
 * statements are true at once and only `delivered` separates them, so this is
 * the field the screen leads with. Showing "Sendt" here would tell someone
 * their invoice reached a customer when nothing left the machine.
 */
export function SendResult({ result }: { readonly result: SendEInvoiceResult }): ReactNode {
  const t = useT();
  if (!result.delivered) {
    return (
      <Alert color="yellow" variant="light" title={t('invDetail.notDeliveredTitle')}>
        <Stack gap="xs">
          <Text size="sm">
            {t('invDetail.notDelivered1a')}
            <strong>{t('invDetail.notDelivered1b')}</strong>
            {t('invDetail.notDelivered1c')}
            <Code>{result.provider}</Code>
            {t('invDetail.notDelivered1d')}
          </Text>
          <Text size="sm">
            {t('invDetail.notDelivered2a')}
            <Code>{result.status}</Code>
            {t('invDetail.notDelivered2b')}
          </Text>
        </Stack>
      </Alert>
    );
  }

  return (
    <Alert color="green" variant="light" title={t('invDetail.deliveredTitle')}>
      <Text size="sm">
        {t('invDetail.delivered', {
          number: result.invoiceNumber,
          format: result.format,
          provider: result.provider,
        })}
        {result.messageId === null
          ? ''
          : t('invDetail.receipt', { messageId: result.messageId })}
      </Text>
    </Alert>
  );
}
